import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { IconArrowLeft } from '@shared/ui/icons';
import { useAgentPermission } from '@entities/user';
import { LOGIST_TASK_KIND_LABELS, type LogistTask } from '@entities/logistics';
import { useListLogistTasksQuery, useAckLogistTaskMutation } from '@shared/api';

/**
 * ОСНОВА.Логист — лента задач: что требует внимания человека прямо сейчас.
 *
 * Задачи вычисляемые: фоновый обмен пересобирает их на каждом тике. Поэтому
 * здесь нет создания и удаления — причина исчезнет, задача закроется сама.
 * «Взял в работу» снимает пометку, но ничего не меняет в документах.
 */

const Wrap = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: 22px;
  font-weight: 600;
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  max-width: 820px;
`;

const Tabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const Tab = styled.button<{ $active: boolean }>`
  font-size: 13px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  cursor: pointer;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.text.secondary)};
`;

const Item = styled.div<{ $severity: LogistTask['severity']; $muted: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 4px solid
    ${({ theme, $severity }) =>
      $severity === 'critical'
        ? theme.colors.status.danger
        : $severity === 'warning'
          ? theme.colors.status.warning
          : theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  opacity: ${({ $muted }) => ($muted ? 0.6 : 1)};
`;

const ItemTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const ItemDetail = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const Meta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  align-items: center;
`;

const SmallBtn = styled.button`
  font-size: 12px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

const Empty = styled.div`
  padding: ${({ theme }) => theme.spacing(6)};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

/** Куда ведёт задача: у каждой сущности свой экран. */
function targetLink(t: LogistTask): string {
  switch (t.entity_kind) {
    case 'trip':
      return '/agents/logist/trips';
    case 'inbox':
    case 'etrn':
    default:
      return '/agents/logist/registry';
  }
}

export function LogistTasks() {
  const { can_write } = useAgentPermission('logist');
  const [onlyOpen, setOnlyOpen] = useState(true);
  const { data, isLoading } = useListLogistTasksQuery(
    onlyOpen ? { state: 'open' } : undefined,
  );
  const [ack, { isLoading: acking }] = useAckLogistTaskMutation();

  const tasks = data?.tasks ?? [];

  return (
    <Wrap>
      <Back to="/agents/logist">
        <IconArrowLeft /> ОСНОВА.Логист
      </Back>
      <Title>
        Задачи
        {data ? ` · ${data.open}${data.critical ? ` (срочных ${data.critical})` : ''}` : ''}
      </Title>
      <Subtitle>
        Здесь собрано то, что не поедет дальше само: накладные, не ушедшие оператору, документы
        без ответа, рейсы с перегрузом. Список пересобирается автоматически — как только
        причина устранена, запись исчезает. «Взял в работу» просто убирает пометку и ничего не
        меняет в документах.
      </Subtitle>

      <Tabs>
        <Tab $active={onlyOpen} onClick={() => setOnlyOpen(true)}>
          Требуют внимания
        </Tab>
        <Tab $active={!onlyOpen} onClick={() => setOnlyOpen(false)}>
          Показать все
        </Tab>
      </Tabs>

      {isLoading ? (
        <Empty>Загрузка…</Empty>
      ) : tasks.length === 0 ? (
        <Empty>
          {onlyOpen
            ? 'Ничего не требует внимания — всё идёт своим ходом.'
            : 'Задач нет.'}
        </Empty>
      ) : (
        tasks.map((t) => (
          <Item key={t.id} $severity={t.severity} $muted={t.state === 'acknowledged'}>
            <ItemTitle>{t.title}</ItemTitle>
            {t.detail && <ItemDetail>{t.detail}</ItemDetail>}
            <Meta>
              <span>{LOGIST_TASK_KIND_LABELS[t.kind] ?? t.kind}</span>
              <span>с {new Date(t.first_seen_at).toLocaleString('ru-RU')}</span>
              <Link to={targetLink(t)} style={{ fontSize: 12 }}>
                Открыть
              </Link>
              {t.state === 'acknowledged' ? (
                <span>в работе</span>
              ) : (
                can_write && (
                  <SmallBtn disabled={acking} onClick={() => ack(t.id)}>
                    Взял в работу
                  </SmallBtn>
                )
              )}
            </Meta>
          </Item>
        ))
      )}
    </Wrap>
  );
}
